<template>
  <div class="vdatetime">
    <input-group v-if="!withoutInputGroup">
      <form-input
        :value="inputValue"
        :name="inputName"
        :readonly="readOnly"
        @focus="open"
      />
      <template slot="prepend">
        <slot name="before" />
      </template>
      <template slot="append">
        <slot name="after" />
      </template>
    </input-group>
    <form-input
      v-else
      :value="inputValue"
      :name="inputName"
      :readonly="readOnly"
      @focus="open"
    />
    <input
      v-if="hiddenName"
      type="hidden"
      :name="hiddenName"
      :value="value"
      @input="setValue"
    >
    <transition-group
      name="vdatetime-fade"
      tag="div"
    >
      <datetime-popup
        v-if="isOpen"
        key="popup"
        :type="type"
        :datetime="popupDate"
        :phrases="phrases"
        :use12-hour="use12Hour"
        :hour-step="hourStep"
        :minute-step="minuteStep"
        :min-datetime="popupMinDatetime"
        :max-datetime="popupMaxDatetime"
        :auto="auto"
        :week-start="weekStart"
        :flow="flow"
        :title="title"
        @confirm="confirm"
        @cancel="cancel"
      >
        <template
          slot="button-cancel__internal"
          slot-scope="scope"
        >
          <slot
            name="button-cancel"
            :step="scope.step"
          >
            {{ phrases.cancel }}
          </slot>
        </template>
        <template
          slot="button-confirm__internal"
          slot-scope="scope"
        >
          <slot
            name="button-confirm"
            :step="scope.step"
          >
            {{ phrases.ok }}
          </slot>
        </template>
      </datetime-popup>
    </transition-group>
  </div>
</template>

<script>
import { DateTime, Settings } from '~luxon'
import DatetimePopup from '../vue-datetime/DatetimePopup'
import { datetimeFromISO, startOfDay } from '../vue-datetime/util'
import FormInput from '~components/common/form/input'
import InputGroup from '~components/common/form/input/Group'

Settings.defaultLocale = 'zh-Tw'

export default {
  name: 'DateTimePicker',
  components: {
    DatetimePopup,
    FormInput,
    InputGroup
  },
  inheritAttrs: false,
  props: {
    value: {
      type: String,
      default: ''
    },
    valueZone: {
      type: String,
      default: 'UTC'
    },
    inputName: {
      type: String,
      default: ''
    },
    inputClass: {
      type: [Object, Array, String],
      default: ''
    },
    inputStyle: {
      type: [Object, Array, String],
      default: ''
    },
    hiddenName: {
      type: String,
      default: ''
    },
    zone: {
      type: String,
      default: 'local'
    },
    format: {
      type: [Object, String],
      default: null
    },
    type: {
      type: String,
      default: 'date'
    },
    phrases: {
      type: Object,
      default () {
        return {
          cancel: '取消',
          ok: '確定'
        }
      }
    },
    use12Hour: {
      type: Boolean,
      default: false
    },
    hourStep: {
      type: Number,
      default: 1
    },
    minuteStep: {
      type: Number,
      default: 1
    },
    minDatetime: {
      type: String,
      default: null
    },
    maxDatetime: {
      type: String,
      default: null
    },
    auto: {
      type: Boolean,
      default: false
    },
    weekStart: {
      type: Number,
      default: 1
    },
    flow: {
      type: Array,
      default: () => ['date']
    },
    title: {
      type: String,
      default: ''
    },
    readOnly: Boolean,
    withoutInputGroup: Boolean
  },
  data () {
    return {
      isOpen: false,
      datetime: datetimeFromISO(this.value)
    }
  },
  computed: {
    inputValue () {
      let format = this.format

      if (!format) {
        switch (this.type) {
          case 'date':
            format = DateTime.DATE_MED // TODO: 汰換 luxon
            break
          case 'time':
            format = DateTime.TIME_24_SIMPLE // TODO: 汰換 luxon
            break
          case 'datetime':
          case 'default':
            format = DateTime.DATETIME_MED // TODO: 汰換 luxon
            break
        }
      }

      if (typeof format === 'string') {
        return this.datetime ? DateTime.fromISO(this.datetime).setZone(this.zone).toFormat(format) : '' // TODO: 汰換 luxon
      } else {
        return this.datetime ? this.datetime.setZone(this.zone).toLocaleString(format) : ''
      }
    },
    popupDate () {
      return this.datetime ? this.datetime.setZone(this.zone) : this.newPopupDatetime()
    },
    popupMinDatetime () {
      return this.minDatetime ? DateTime.fromISO(this.minDatetime).setZone(this.zone) : null // TODO: 汰換 luxon
    },
    popupMaxDatetime () {
      return this.maxDatetime ? DateTime.fromISO(this.maxDatetime).setZone(this.zone) : null // TODO: 汰換 luxon
    }
  },
  watch: {
    value (newValue) {
      this.datetime = datetimeFromISO(newValue)
    },
    isOpen (value) {
      if (value) {
        this.$store.commit('layouts/setModalBackdropOpen')
      } else {
        this.$store.commit('layouts/setModalBackdropClose')
      }
    }
  },
  created () {
    this.emitInput()
    this.$store.commit('layouts/setModalBackdropClose')
  },
  methods: {
    emitInput () {
      let datetime = this.datetime ? this.datetime.setZone(this.valueZone) : null

      if (datetime && this.type === 'date') {
        datetime = startOfDay(datetime)
      }

      this.$emit('input', datetime ? datetime.toISO() : '')
    },
    open (event) {
      event.target.blur()
      if (!this.readOnly) {
        this.isOpen = true
      }
    },
    close () {
      this.isOpen = false
      this.$emit('close')
    },
    confirm (datetime) {
      this.datetime = datetime.toUTC()
      this.emitInput()
      this.close()
    },
    cancel () {
      this.close()
    },
    newPopupDatetime () {
      let datetime = DateTime.utc().setZone(this.zone).set({ seconds: 0, milliseconds: 0 }) // TODO: 汰換 luxon

      if (this.popupMinDatetime && datetime < this.popupMinDatetime) {
        datetime = this.popupMinDatetime.set({ seconds: 0, milliseconds: 0 })
      }

      if (this.popupMaxDatetime && datetime > this.popupMaxDatetime) {
        datetime = this.popupMaxDatetime.set({ seconds: 0, milliseconds: 0 })
      }

      if (this.minuteStep === 1) {
        return datetime
      }

      const roundedMinute = Math.round(datetime.minute / this.minuteStep) * this.minuteStep

      if (roundedMinute === 60) {
        return datetime.plus({ hours: 1 }).set({ minute: 0 })
      }

      return datetime.set({ minute: roundedMinute })
    },
    setValue (event) {
      this.datetime = datetimeFromISO(event.target.value)
      this.emitInput()
    }
  }
}
</script>
<style scoped>
  @import '~vue-datetime/dist/vue-datetime.css';
</style>

<style>
.vdatetime {
  flex: 1;
}
.vdatetime-fade-enter-active,
.vdatetime-fade-leave-active {
  transition: opacity .4s;
}

.vdatetime-fade-enter,
.vdatetime-fade-leave-to {
  opacity: 0;
}
.vdatetime-popup {
    z-index: 1041;
}
.vdatetime-calendar__navigation--previous{
    z-index: 100;
}
</style>
